import axios from "axios";
import { appUrl } from "../../../../../../utils/config";
import { staticTabData } from "./tabStaticData";

/**
 * Helper function to get API base URL
 */
const getApiBaseUrl = () => {
  const appName = localStorage.getItem("appName");
  const network = localStorage.getItem("network");
  const collabType = localStorage.getItem("collabType");
  const roleName = localStorage.getItem("roleName");
  
  if (appName && network && collabType && roleName) {
    return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
  }
  return null;
};

/**
 * Fetch Tab Configuration
 * GET /fetchTabConfiguration/{collabrixCampaignID}
 * 
 * @param {string|number} viewType - 'systemView' or 'overallView' or 1 or 2
 * @param {string} collabrixCampaignID - Campaign ID
 * @returns {Promise} Response with tabConfigurationList
 */
// Tabs that have static Excel data. Served as the tab list while the
// tab-configuration backend is not yet wired up (API integration pending).
const STATIC_TAB_CONFIGURATION = [
  "Materials & Consumption",
  "In-Process Tests",
  "Product Release Tests",
  "Deviations",
  "Change Controls",
  "Equipment",
  "Yield",
].map((tabName) => ({ tabName }));

const staticTabConfigResponse = () => ({
  status: true,
  data: { tabConfigurationList: STATIC_TAB_CONFIGURATION },
});

// Master switch for ALL Product Development static data on the frontend. This repo
// has no env-var mechanism (it is a component library built by the parent app), so
// this is a plain constant.
//   true  = serve bundled static data (live env branch: test/3.1.3_live_pd)
//   false = make every PD tab read from the backend instead (this test branch):
//           - the tab list comes from /fetchTabConfiguration (fetchTabConfigurationAPI)
//           - tab row data comes from the backend APIs instead of the bundled Excel
//             data (see getTabAPI)
// NOTE: routing to Corda also requires USE_MOCK_DATA=false in the njm backend
// (controllers/BMS/mockData.js), otherwise the backend still returns its own mock data.
export const USE_STATIC_PD_DATA = false;

// Force the authoritative static tab list (includes static-only tabs Equipment, Yield
// that the backend tab config omits). Driven by the master switch above.
const FORCE_STATIC_TAB_CONFIG = USE_STATIC_PD_DATA;

// No view parameter: System View is the only view, so the campaign id alone
// identifies the configuration. The endpoint previously took a leading
// 1 = overallView / 2 = systemView segment; that param is gone from this call
// (the backend still accepts and ignores the old form for other callers).
// Passing 1 there asked for overallView, which — now that overallView rows are
// no longer written — could only return an empty list, and the static fallback
// below would then quietly show every tab as if all were configured.
export const fetchTabConfigurationAPI = async (collabrixCampaignID) => {
  const apiBaseUrl = getApiBaseUrl();

  // Serve the static tab list (forced for the demo, or when no backend is available).
  if (FORCE_STATIC_TAB_CONFIG || !apiBaseUrl || !collabrixCampaignID) {
    return staticTabConfigResponse();
  }

  try {
    console.log(`[TabConfigAPI] GET /fetchTabConfiguration/${collabrixCampaignID}`);
    const response = await axios.get(
      `${apiBaseUrl}/fetchTabConfiguration/${collabrixCampaignID}`
    );

    console.log(`[TabConfigAPI] Response:`, response.data);

    if (response?.data) {
      // Pull the tab list out of whichever response shape the backend used.
      const list =
        (Array.isArray(response.data.tabConfigurationList) && response.data.tabConfigurationList) ||
        (response.data.data && Array.isArray(response.data.data.tabConfigurationList) && response.data.data.tabConfigurationList) ||
        [];
      // TEMP: tab config is not populated for most campaigns yet — the backend returns an
      // empty list. Only trust a non-empty list; an empty one falls through to the full
      // static tab set below so all tabs show by default. Remove once tab config is wired up.
      if (list.length > 0) {
        return { status: true, data: { tabConfigurationList: list } };
      }
    }

    // Backend reachable but returned nothing usable / an empty list → show all tabs.
    return staticTabConfigResponse();
  } catch (error) {
    console.error("Error fetching tab configuration, using static tabs:", error);
    return staticTabConfigResponse();
  }
};

/**
 * Fetch Materials & Consumption
 * POST /fetchMaterialConsumption (per API contract)
 */
export const fetchMaterialAndConsumptionAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchMaterialConsumption');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchMaterialConsumption`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures:
    // Structure 1: { status: true, message: "...", materialAndConsumptionList: [...] }
    // Structure 2: { data: { status: true, data: { materialAndConsumptionList: [...] } } }
    if (response?.data) {
      // Direct structure (status and materialAndConsumptionList at root level)
      if (response.data.status && response.data.materialAndConsumptionList) {
        return response.data;
      }
      // Nested structure
      if (response.data.status && response.data.data && response.data.data.materialAndConsumptionList) {
        return response.data;
      }
      // If materialAndConsumptionList exists directly, wrap it
      if (response.data.materialAndConsumptionList) {
        return {
          status: true,
          data: { materialAndConsumptionList: response.data.materialAndConsumptionList }
        };
      }
    }
    
    return { status: false, data: { materialAndConsumptionList: [] } };
  } catch (error) {
    console.error("Error fetching Material and Consumption data:", error);
    throw error;
  }
};

/**
 * Fetch CPP Details
 * POST /fetchCPPDetails
 */
export const fetchCPPDetailsAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchCPPDetails');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchCPPDetails`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures:
    // Structure 1: { status: true, message: "...", cppDetailsList: [...] }
    // Structure 2: { data: { status: true, data: { cppDetailsList: [...] } } }
    if (response?.data) {
      // Direct structure (status and cppDetailsList at root level)
      if (response.data.status && response.data.cppDetailsList) {
        return response.data;
      }
      // Nested structure
      if (response.data.status && response.data.data && response.data.data.cppDetailsList) {
        return response.data;
      }
      // If cppDetailsList exists directly, wrap it
      if (response.data.cppDetailsList) {
        return {
          status: true,
          data: { cppDetailsList: response.data.cppDetailsList }
        };
      }
    }
    
    return { status: false, data: { cppDetailsList: [] } };
  } catch (error) {
    console.error("Error fetching CPP details:", error);
    throw error;
  }
};

/**
 * Fetch In-Process Tests
 * POST /fetchInProcessTest
 */
export const fetchInProcessTestAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchInProcessTest');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchInProcessTest`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures
    if (response?.data) {
      if (response.data.status && response.data.inProcessTestList) {
        return response.data;
      }
      if (response.data.status && response.data.data && response.data.data.inProcessTestList) {
        return response.data;
      }
      if (response.data.inProcessTestList) {
        return {
          status: true,
          data: { inProcessTestList: response.data.inProcessTestList }
        };
      }
    }
    
    return { status: false, data: { inProcessTestList: [] } };
  } catch (error) {
    console.error("Error fetching in-process tests:", error);
    throw error;
  }
};

/**
 * Fetch Product Release Tests
 * POST /fetchProductReleaseTest
 */
export const fetchProductReleaseTestAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchProductReleaseTest');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchProductReleaseTest`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures
    if (response?.data) {
      if (response.data.status && response.data.productReleaseTestList) {
        return response.data;
      }
      if (response.data.status && response.data.data && response.data.data.productReleaseTestList) {
        return response.data;
      }
      if (response.data.productReleaseTestList) {
        return {
          status: true,
          data: { productReleaseTestList: response.data.productReleaseTestList }
        };
      }
    }
    
    return { status: false, data: { productReleaseTestList: [] } };
  } catch (error) {
    console.error("Error fetching product release tests:", error);
    throw error;
  }
};

/**
 * Fetch Deviations
 * POST /fetchDeviations
 */
export const fetchDeviationsAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchDeviations');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchDeviations`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures.
    // Contract response key is `deviationsList` (legacy `deviationList` tolerated).
    const pickDeviations = (obj) => obj?.deviationsList || obj?.deviationList;
    if (response?.data) {
      if (response.data.status && pickDeviations(response.data)) {
        return response.data;
      }
      if (response.data.status && response.data.data && pickDeviations(response.data.data)) {
        return response.data;
      }
      if (pickDeviations(response.data)) {
        return {
          status: true,
          data: { deviationsList: pickDeviations(response.data) }
        };
      }
    }

    return { status: false, data: { deviationsList: [] } };
  } catch (error) {
    console.error("Error fetching deviations:", error);
    throw error;
  }
};

/**
 * Fetch Planned Change
 * POST /fetchPlannedChange (per API contract)
 * Response key: changeControlList
 */
export const fetchPlannedChangeAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchPlannedChange');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(`${apiBaseUrl}/fetchPlannedChange`, payload);

    console.log('📡 Response:', response.data);

    const pick = (obj) => obj?.plannedChangeList || obj?.changeControlList;
    if (response?.data) {
      if (response.data.status && pick(response.data)) {
        return response.data;
      }
      if (response.data.status && response.data.data && pick(response.data.data)) {
        return response.data;
      }
      if (pick(response.data)) {
        return { status: true, data: { changeControlList: pick(response.data) } };
      }
    }

    return { status: false, data: { changeControlList: [] } };
  } catch (error) {
    console.error("Error fetching planned changes:", error);
    throw error;
  }
};

/**
 * Fetch Equipments
 * POST /fetchEquipments (per API contract)
 * Response key: equipmentList
 */
export const fetchEquipmentsAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchEquipments');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(`${apiBaseUrl}/fetchEquipments`, payload);

    console.log('📡 Response:', response.data);

    if (response?.data) {
      if (response.data.status && response.data.equipmentList) {
        return response.data;
      }
      if (response.data.status && response.data.data && response.data.data.equipmentList) {
        return response.data;
      }
      if (response.data.equipmentList) {
        return { status: true, data: { equipmentList: response.data.equipmentList } };
      }
    }

    return { status: false, data: { equipmentList: [] } };
  } catch (error) {
    console.error("Error fetching equipments:", error);
    throw error;
  }
};

/**
 * Fetch Batch Yield
 * POST /fetchBatchYield (per API contract)
 * Response key: yieldList
 */
export const fetchBatchYieldAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchBatchYield');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(`${apiBaseUrl}/fetchBatchYield`, payload);

    console.log('📡 Response:', response.data);

    // Contract response wrapper is labelled `equipmentList` (copy-paste in the
    // spec); tolerate both that and the sensible `yieldList`.
    const pick = (obj) => obj?.yieldList || obj?.batchYieldList || obj?.equipmentList;
    if (response?.data) {
      if (response.data.status && pick(response.data)) {
        return response.data;
      }
      if (response.data.status && response.data.data && pick(response.data.data)) {
        return response.data;
      }
      if (pick(response.data)) {
        return { status: true, data: { yieldList: pick(response.data) } };
      }
    }

    return { status: false, data: { yieldList: [] } };
  } catch (error) {
    console.error("Error fetching batch yield:", error);
    throw error;
  }
};

/**
 * Fetch Change Controls
 * POST /fetchChangeControls
 */
export const fetchChangeControlsAPI = async (payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log('📡 POST API: fetchChangeControls');
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/fetchChangeControls`,
      payload
    );
    
    console.log('📡 Response:', response.data);
    
    // Handle different response structures
    if (response?.data) {
      if (response.data.status && response.data.changeControlList) {
        return response.data;
      }
      if (response.data.status && response.data.data && response.data.data.changeControlList) {
        return response.data;
      }
      if (response.data.changeControlList) {
        return {
          status: true,
          data: { changeControlList: response.data.changeControlList }
        };
      }
    }
    
    return { status: false, data: { changeControlList: [] } };
  } catch (error) {
    console.error("Error fetching change controls:", error);
    throw error;
  }
};

/**
 * Convert tab name to API endpoint name
 * Examples: "Material & Consumption" -> "fetchMaterialAndConsumption"
 *           "Batch Records" -> "fetchBatchRecords"
 *           "Quality Events" -> "fetchQualityEvents"
 */
const tabNameToApiEndpoint = (tabName) => {
  // Handle special cases first (endpoint names per API contract).
  const specialCases = {
    "Material & Consumption": "fetchMaterialConsumption",
    "Materials & Consumption": "fetchMaterialConsumption",
    "CPP Details": "fetchCPPDetails",
    "CPPs": "fetchCPPDetails",
    "In Process Test": "fetchInProcessTest",
    "In-Process Tests": "fetchInProcessTest",
    "Product Release Test": "fetchProductReleaseTest",
    "Product Release Tests": "fetchProductReleaseTest",
    "Deviations": "fetchDeviations",
    "Change Controls": "fetchChangeControls",
    "CC": "fetchChangeControls",
    "Planned Change": "fetchPlannedChange",
    "Planned Changes": "fetchPlannedChange",
    "Equipment": "fetchEquipments",
    "Yield": "fetchBatchYield",
    "Batch Yield": "fetchBatchYield",
  };

  if (specialCases[tabName]) {
    return specialCases[tabName];
  }
  
  // Generic conversion: "Batch Records" -> "fetchBatchRecords"
  // Remove special characters, convert to camelCase, prefix with "fetch"
  const cleaned = tabName
    .replace(/[&]/g, '') // Remove &
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
  
  const words = cleaned.split(' ');
  const camelCase = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toLowerCase() + word.slice(1);
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
  
  return `fetch${camelCase.charAt(0).toUpperCase()}${camelCase.slice(1)}`;
};

/**
 * Generic API function that dynamically calls any tab API
 */
const callGenericTabAPI = async (endpointName, payload) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error("API base URL not available");
  }

  try {
    console.log(`📡 POST API: ${endpointName}`);
    console.log('Payload:', JSON.stringify(payload, null, 2));
    const response = await axios.post(
      `${apiBaseUrl}/${endpointName}`,
      payload
    );
    
    console.log(`📡 Response for ${endpointName}:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${endpointName}:`, error);
    throw error;
  }
};

/**
 * Filter a static tab response by collabrixBatchID from the payload.
 * Finds the list array in the response object and filters it.
 */
const filterStaticData = (data, payload) => {
  const batchID = payload?.collabrixBatchID;
  if (!batchID) return data;

  // Find the list key (e.g., materialAndConsumptionList, inProcessTestList, ...)
  const listKey = Object.keys(data).find(
    (k) => Array.isArray(data[k])
  );
  if (!listKey) return data;

  const filtered = data[listKey].filter(
    (item) => item.collabrixBatchID === batchID
  );

  console.log(
    `[TabAPI] Filtered ${listKey}: ${data[listKey].length} → ${filtered.length} rows (batchID: ${batchID})`
  );

  return { ...data, [listKey]: filtered };
};

/**
 * Map tab name to API function - Fully dynamic
 * Uses static data from Excel when available, falls back to real API calls
 */
export const getTabAPI = (tabName) => {
  // Resolve alternate tab name variants
  const nameVariants = {
    "Material & Consumption": "Materials & Consumption",
    "In Process Test": "In-Process Tests",
    "Product Release Test": "Product Release Tests",
    "CPP Details": "CPPs",
    "CC": "Change Controls",
  };
  const resolvedName = nameVariants[tabName] || tabName;

  // If static data exists for this tab, return filtered by batchID.
  // Gated by the master switch so going live routes every tab to the backend.
  if (USE_STATIC_PD_DATA && staticTabData[resolvedName]) {
    console.log(`[TabAPI] Using static Excel data for tab: "${tabName}"`);
    return async (payload) => filterStaticData(staticTabData[resolvedName], payload);
  }

  // CPPs: no data in Excel, return empty (only while running on static data;
  // when live this falls through to the real fetchCPPDetails backend call).
  if (USE_STATIC_PD_DATA && (resolvedName === "CPPs" || resolvedName === "CPP Details")) {
    console.log(`[TabAPI] No Excel data for CPPs, returning empty`);
    return async () => ({ status: true, cppDetailsList: [] });
  }

  // Change Controls tab: the table merges TWO backend sources —
  // fetchChangeControls rows first, then fetchPlannedChange rows appended.
  // Both lists are returned in one response so the mapper renders them in a
  // single table. (Live path only; static demo data is handled above.)
  if (resolvedName === "Change Controls") {
    return async (payload) => {
      const [ccRes, pcRes] = await Promise.all([
        fetchChangeControlsAPI(payload).catch((e) => {
          console.error('[TabAPI] fetchChangeControls failed:', e);
          return null;
        }),
        fetchPlannedChangeAPI(payload).catch((e) => {
          console.error('[TabAPI] fetchPlannedChange failed:', e);
          return null;
        }),
      ]);

      // The two endpoints return their list under different keys
      // (changeControlList vs plannedChangeList) and at different nesting
      // levels, so check all of them.
      const listFrom = (res) =>
        res?.plannedChangeList ||
        res?.changeControlList ||
        res?.data?.plannedChangeList ||
        res?.data?.changeControlList ||
        res?.data?.data?.plannedChangeList ||
        res?.data?.data?.changeControlList ||
        [];

      return {
        status: true,
        // Rendered first by mapChangeControlsToTable.
        changeControlList: listFrom(ccRes),
        // Appended after the change-control rows.
        plannedChangeList: listFrom(pcRes),
      };
    };
  }

  // For any other tab, create a dynamic API function
  const endpointName = tabNameToApiEndpoint(tabName);
  console.log(`[TabAPI] Creating dynamic API for tab "${tabName}" -> endpoint: ${endpointName}`);

  return async (payload) => {
    return await callGenericTabAPI(endpointName, payload);
  };
};

/**
 * Get tab names that have static data available (for supplementing API response)
 */
export const getStaticTabNames = () => {
  return Object.keys(staticTabData);
};

/**
 * Map tab name to response data key
 */
export const getTabResponseKey = (tabName) => {
  const responseKeyMap = {
    "Materials & Consumption": "materialAndConsumptionList",
    "CPPs": "cppDetailsList",
    "In-Process Tests": "inProcessTestList",
    "Product Release Tests": "productReleaseTestList",
    "Deviations": "deviationsList",
    "Change Controls": "changeControlList",
    "CC": "changeControlList",
    "Planned Change": "changeControlList",
    "Planned Changes": "changeControlList",
    "Equipment": "equipmentList",
    "Yield": "yieldList",
    "Batch Yield": "yieldList",
  };

  return responseKeyMap[tabName] || null;
};
